// src/components/GuestForm.js
import React, { useState } from 'react';
import { TextField, MenuItem, Select, Button, Grid, FormControl, InputLabel, Box, Typography, IconButton, Collapse } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// Form for a single family member
const FamilyMemberFields = ({ index, familyMember, onChange, onRemove }) => (
  <div style={{ marginBottom: '16px' }}>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Name of Family Member"
          variant="outlined"
          name="name"
          value={familyMember.name}
          onChange={(e) => onChange(e, index)}
          data-family-index={index}
          required
          InputLabelProps={{
            style: { fontSize: "1.5rem" }, 
          }}
          sx={{
           
            "& .MuiInputLabel-root.Mui-focused": {
              transform: "translate(14px, -4px) scale(0.5)", // ✅ Adjust position when focused
            },
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Age"
          variant="outlined"
          name="age"
          value={familyMember.age}
          onChange={(e) => onChange(e, index)}
          data-family-index={index}
          type="number"
          required
          InputLabelProps={{
            style: { fontSize: "1.5rem" }, 
          }}
          sx={{
           
            "& .MuiInputLabel-root.Mui-focused": {
              transform: "translate(14px, -4px) scale(0.5)", // ✅ Adjust position when focused
            },
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel style= {{ fontSize: "1.5rem" }}>Gender</InputLabel>
          <Select
            name="gender"
            value={familyMember.gender}
            onChange={(e) => onChange(e, index)}
            data-family-index={index}
            label="Gender"
            required
            sx={{
           
              "& .MuiInputLabel-root.Mui-focused": {
                transform: "translate(14px, -4px) scale(0.5)", // ✅ Adjust position when focused
              },
            }}
          >
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Relationship with Bride/Groom"
          variant="outlined"
          name="relationship"
          value={familyMember.relationship}
          onChange={(e) => onChange(e, index)}
          data-family-index={index}
          required
          InputLabelProps={{
            style: { fontSize: "1.5rem" }, 
          }}
          sx={{
           
            "& .MuiInputLabel-root.Mui-focused": {
              transform: "translate(14px, -4px) scale(0.5)", // ✅ Adjust position when focused
            },
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Contact Number"
          variant="outlined"
          name="contact"
          value={familyMember.contact}
          onChange={(e) => onChange(e, index)}
          data-family-index={index}
          required
          type="tel"
          InputLabelProps={{
            style: { fontSize: "1.5rem" }, 
          }}
          sx={{
           
            "& .MuiInputLabel-root.Mui-focused": {
              transform: "translate(14px, -4px) scale(0.5)", // ✅ Adjust position when focused
            },
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Email"
          variant="outlined"
          name="email"
          value={familyMember.email}
          onChange={(e) => onChange(e, index)}
          data-family-index={index}
          required
          type="email"
          InputLabelProps={{
            style: { fontSize: "1.5rem" }, 
          }}
          sx={{
           
            "& .MuiInputLabel-root.Mui-focused": {
              transform: "translate(14px, -4px) scale(0.5)", // ✅ Adjust position when focused
            },
          }}
        />
      </Grid>
    </Grid>

    {/* Remove Family Member Button */}
    <IconButton
      color="error"
      onClick={() => onRemove(index)}
      sx={{ marginTop: 2 }}
    >
      <RemoveCircleIcon />
    </IconButton>
  </div>
);

const GuestForm = ({onSubmit}) => {
  // Initial state setup
  const [formData, setFormData] = useState({
    guest_name: '',
    age: '',
    gender: '',
    relationship: '',
    contact: '',
    email: '',
    family_members: [] // Start with no family members
  });

  // State for collapse functionality
  const [collapsed, setCollapsed] = useState(false);

  // Handle changes for guest and family members
  const handleChange = (e, familyIndex = null) => {
    const { name, value } = e.target;
  
    if (familyIndex !== null) {
      // Ensure the family member exists before modifying it
      setFormData((prev) => {
        const updatedFamilyMembers = [...prev.family_members];
  
        if (!updatedFamilyMembers[familyIndex]) {
          updatedFamilyMembers[familyIndex] = {
            guest_name: "",
            age: "",
            relationship: "",
            gender: "",
            contact: "",
            email: "",
          };
        }
  
        updatedFamilyMembers[familyIndex] = {
          ...updatedFamilyMembers[familyIndex],
          [name]: value, // ✅ Updates gender correctly
        };
  
        return { ...prev, family_members: updatedFamilyMembers };
      });
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };
  

  // Add new family member (Limit to 6)
  const addFamilyMember = () => {
    if (formData.family_members.length < 6) {
      setFormData({
        ...formData,
        family_members: [
          ...formData.family_members,
          { name: '', age: '', gender: '', relationship: '', contact: '', email: '' }
        ]
      });
    }
  };

  // Remove a family member
  const removeFamilyMember = (index) => {
    const updatedFamilyMembers = formData.family_members.filter((_, i) => i !== index);
    setFormData({ ...formData, family_members: updatedFamilyMembers });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    console.log('Form submitted:', formData);
  };

  return (
    <Box sx={{ width: '80%', maxWidth: '100%', margin: 'auto', padding: { xs: 2, sm: 3, md: 4 } }}>
      <Typography variant="h4" gutterBottom textAlign="center">
        Guest Information
      </Typography>

      {/* Collapse button to toggle form visibility */}
      <IconButton onClick={() => setCollapsed(!collapsed)} sx={{ marginBottom: 2 }}>
        {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
      </IconButton>

      {/* Collapsible Form */}
      <Collapse in={!collapsed}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2} style={{marginBottom:"20px"}}>
            <Grid item xs={12} >
              <TextField
                fullWidth
                label="Name of Guest"
                variant="outlined"
                name="guest_name"
                value={formData.guest_name}
                onChange={handleChange}
                required
                InputLabelProps={{
                  style: { fontSize: "1.5rem" }, 
                }}
                sx={{
                 
                  "& .MuiInputLabel-root.Mui-focused": {
                    transform: "translate(14px, -7px) scale(0.5)", // ✅ Adjust position when focused
                  },
                  
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Age"
                variant="outlined"
                name="age"
                value={formData.age}
                onChange={handleChange}
                type="number"
                required
                InputLabelProps={{
                  style: { fontSize: "1.5rem" }, 
                }}
                sx={{
                 
                  "& .MuiInputLabel-root.Mui-focused": {
                    transform: "translate(14px, -7px) scale(0.5)", // ✅ Adjust position when focused
                  },
                  
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel style= {{ fontSize: "1.5rem" }}>Gender</InputLabel>
                <Select
                  name="gender"
                  value={formData.gender}
                  onChange={(e) => handleChange(e)} // ✅ Pass index properly
                  label="Gender"
                  required
                  
                  sx={{
                   
                    "& .MuiInputLabel-root.Mui-focused": {
                      transform: "translate(14px, -4px) scale(0.5)", // ✅ Adjust position when focused
                    },
                    
                  }}
                >
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Relationship with Bride/Groom"
                variant="outlined"
                name="relationship"
                value={formData.relationship}
                onChange={handleChange}
                required
                InputLabelProps={{
                  style: { fontSize: "1.5rem" }, 
                }}
                sx={{
                 
                  "& .MuiInputLabel-root.Mui-focused": {
                    transform: "translate(14px, -4px) scale(0.5)", // ✅ Adjust position when focused
                  },
                  
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Number"
                variant="outlined"
                name="contact"
                value={formData.contact}
                onChange={handleChange}
                required
                type="tel"
                InputLabelProps={{
                  style: { fontSize: "1.5rem" }, 
                }}
                sx={{
                 
                  "& .MuiInputLabel-root.Mui-focused": {
                    transform: "translate(14px, -4px) scale(0.5)", // ✅ Adjust position when focused
                  },
                  
                }}
                
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                variant="outlined"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                type="email"

                InputLabelProps={{
                  style: { fontSize: "1.5rem" }, 
                }}
                sx={{
           
                  "& .MuiInputLabel-root.Mui-focused": {
                    transform: "translate(14px, -5px) scale(0.5)", // ✅ Adjust position when focused
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* Family Members Section */}
          {formData.family_members.length > 0 && (
            <div>
              <Typography variant="h5" gutterBottom textAlign="center">
                Family Members
              </Typography>
              {formData.family_members.map((familyMember, index) => (
                <FamilyMemberFields
                  key={index}
                  index={index}
                  familyMember={familyMember}
                  onChange={handleChange}
                  onRemove={removeFamilyMember}
                             
                />
              ))}
            </div>
          )}

          {/* Add Family Member Button */}
          {formData.family_members.length < 6 && (
            <Button
              variant="outlined"
              color="primary"
              onClick={addFamilyMember}
              startIcon={<AddCircleIcon />}
              fullWidth
              sx={{ marginBottom: 2 }}
              disabled={formData.family_members.length >= 6}
            >
              Add Family Member
            </Button>
          )}

          {/* Submit Button */}
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ padding: 1.5 }}
            >
              Submit
            </Button>
          </Grid>
        </form>
      </Collapse>
    </Box>
  );
};

export default GuestForm;
